'use client';

import {
  PublicClientApplication,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';

import { getMsalConfig } from './authConfig';
import { getRuntimeConfig } from './runtime-config';

/** Sign-in only. User.Read is a Graph permission: right for identity, wrong for our APIs. */
export const entraLoginScopes = ['User.Read'];

/**
 * The scope our app registration exposes. Asking for User.Read here yields a token minted FOR
 * Graph - aud is Graph's id, issuer v1, signed with Graph's keys - which our validators reject,
 * correctly. The symptom is a signature failure that looks like a broken app registration.
 *
 * Built per call because the client id is runtime configuration.
 */
export const getEntraApiScopes = (): string[] => {
  const clientId = getRuntimeConfig().azureClientId;

  if (!clientId) {
    throw new Error(
      'AZURE_CLIENT_ID is not configured, so no Okahu API scope can be requested. Without it MSAL would be asked for "api://undefined/user_impersonation".'
    );
  }

  return [`api://${clientId}/user_impersonation`];
};

let msalInstance: IPublicClientApplication | null = null;
let initializationPromise: Promise<IPublicClientApplication> | null = null;

export function getMsalInstance(): Promise<IPublicClientApplication> {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new Error('MSAL can only be initialized in the browser')
    );
  }

  if (msalInstance) {
    return Promise.resolve(msalInstance);
  }

  if (!initializationPromise) {
    initializationPromise = (async () => {
      const instance = new PublicClientApplication(getMsalConfig());

      await instance.initialize();

      // Must run before any consumer checks for an account: after the Microsoft redirect,
      // accounts are not in the cache until this resolves.
      const response = await instance.handleRedirectPromise();
      const account = response?.account ?? instance.getAllAccounts()[0];
      if (account) {
        instance.setActiveAccount(account);
      }

      msalInstance = instance;

      return instance;
    })();
  }

  return initializationPromise;
}

export async function getEntraAccessToken(): Promise<string> {
  const instance = await getMsalInstance();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

  if (!account) {
    throw new Error('Microsoft sign-in is required before making API requests');
  }

  // The API scope, NOT the login scope - see getEntraApiScopes.
  const result = await instance.acquireTokenSilent({
    scopes: getEntraApiScopes(),
    account,
  });

  return result.accessToken;
}

export async function getEntraAccount(): Promise<AccountInfo | null> {
  const instance = await getMsalInstance();

  return instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
}

export async function signOutOfMicrosoft(): Promise<void> {
  const instance = await getMsalInstance();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

  await instance.logoutRedirect({
    account,
    postLogoutRedirectUri: '/',
  });
}
